import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft, Plus, Edit3, Trash2, Eye, GraduationCap, Users, FileText, FileQuestion,
  TrendingUp, Euro, Search, Upload, Video, Save, X, CheckCircle2, Clock, Award, Filter,
  ChevronRight, Settings, BookOpen, BarChart3, LogOut, ShieldCheck, FileBadge2,
  CreditCard, XCircle, CalendarDays, Wallet, Truck, Package, Stamp, Download, IdCard, Mail, Send,
  Sparkles, Calendar, Newspaper, Handshake,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LOGO_URL } from "../data/mock";
import { MOCK_ADMIN_CORSI, MOCK_ADMIN_ISCRITTI, MOCK_ADMIN_STATS, MOCK_CORSI } from "../data/dashboardMock";
import RegistroAdminSection from "./RegistroAdminSection";
import AdminVenditeTab from "./AdminVenditeTab";
import AdminApprovazioniRegistro from "./AdminApprovazioniRegistro";
import BlogAdminSection from "./BlogAdminSection";
import EventiAdminSection from "./EventiAdminSection";
import PartnersAdminSection from "./PartnersAdminSection";
import FormazioneAdminSection from "./FormazioneAdminSection";

export default function AdminPanel() {
  const [section, setSection] = useState("dashboard");
  const [editingCorso, setEditingCorso] = useState(null);
  const [openCorso, setOpenCorso] = useState(null);
  const [authStatus, setAuthStatus] = useState("loading"); // loading | ok | forbidden

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.role === "admin") setAuthStatus("ok");
        else setAuthStatus("forbidden");
      })
      .catch(() => {
        if (!cancelled) setAuthStatus("forbidden");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authStatus === "loading") {
    return (
      <div
        data-testid="admin-guard-loading"
        className="min-h-screen bg-[#0e1424] text-white flex items-center justify-center"
      >
        <div className="text-slate-300 text-sm">Verifica accesso amministratore…</div>
      </div>
    );
  }
  if (authStatus === "forbidden") {
    return (
      <div
        data-testid="admin-guard-forbidden"
        className="min-h-screen bg-[#0e1424] text-white flex items-center justify-center p-6"
      >
        <div className="max-w-md text-center space-y-4">
          <div className="text-xs font-extrabold uppercase tracking-widest text-[#f59e0b]">
            Accesso riservato
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold">
            Area accessibile solo agli amministratori
          </h1>
          <p className="text-slate-300 text-sm">
            Effettua il login con un account con ruolo amministratore per visualizzare il pannello.
          </p>
          <Link
            to="/area-soci"
            data-testid="admin-guard-login-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold text-sm"
          >
            Vai al login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e1424] flex">
      {/* Sidebar admin scura */}
      <aside className="w-72 bg-[#0a1020] border-r border-white/5 flex flex-col text-white sticky top-0 h-screen">
        <div className="p-5 border-b border-white/5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white p-1.5"><img src={LOGO_URL} alt="ACDT" className="h-full w-full object-contain" /></div>
          <div>
            <div className="font-extrabold">A.C.D.T.</div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#f59e0b]">Admin Panel</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <div className="px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-500">Panello ACDT</div>
          {[
            { id: "dashboard", icon: BarChart3, label: "Dashboard" },
            { id: "corsi", icon: GraduationCap, label: "Gestione Corsi" },
            { id: "iscritti", icon: Users, label: "Iscritti & Progressi" },
            { id: "vendite", icon: TrendingUp, label: "Vendite & Fatture", badge: "Novità" },
            { id: "soci-anagrafica", icon: IdCard, label: "Anagrafica Soci" },
            { id: "corso-admin", icon: GraduationCap, label: "Corso A.C.D.T." },
            { id: "formazione-acdt", icon: GraduationCap, label: "Formazione → Corsisti", badge: "Nuovo" },
            { id: "newsletter", icon: Mail, label: "Newsletter" },
            { id: "blog", icon: Newspaper, label: "Blog & News", badge: "SEO" },
            { id: "eventi", icon: Calendar, label: "Eventi", badge: "Stripe" },
            { id: "partners", icon: Handshake, label: "Partner Extra", badge: "Nuovo" },
            { id: "approvazioni", icon: ShieldCheck, label: "Approvazioni Registro" },
            { id: "bonifici", icon: Wallet, label: "Bonifici" },
            { id: "spedizioni", icon: Truck, label: "Spedizioni Materiale" },
            { id: "registro", icon: FileBadge2, label: "Registro Esse Studi" },
            { id: "quiz", icon: FileQuestion, label: "Quiz & Test" },
            { id: "materiali", icon: FileText, label: "Materiali" },
            { id: "abtest", icon: TrendingUp, label: "A/B Test CTA" },
            { id: "reminder", icon: Mail, label: "Reminder Email" },
            { id: "campagne", icon: Send, label: "Campagne Email" },
            { id: "impostazioni", icon: Settings, label: "Impostazioni" },
          ].map((item) => {
            const I = item.icon;
            const active = section === item.id;
            return (
              <button
                key={item.id}
                data-testid={`admin-nav-${item.id}`}
                onClick={() => { setSection(item.id); setEditingCorso(null); setOpenCorso(null); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-semibold transition-colors ${active ? "bg-[#f59e0b] text-[#0a1020]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
              >
                <I className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}

          <div className="pt-4 px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#f59e0b]">Gestionale</div>
          {[
            { id: "gest-home", icon: BarChart3, label: "Home Gestionale" },
            { id: "gest-soci", icon: Users, label: "Soci" },
            { id: "gest-personale", icon: Users, label: "Personale Esterno" },
            { id: "gest-affiliati", icon: ShieldCheck, label: "Affiliati" },
            { id: "gest-amministrazione", icon: Wallet, label: "Amministrazione" },
            { id: "gest-tessere", icon: IdCard, label: "Tessere" },
            { id: "gest-attivita", icon: Sparkles, label: "Attività" },
            { id: "gest-documenti", icon: FileText, label: "Documenti" },
            { id: "gest-formazione", icon: GraduationCap, label: "Formazione" },
            { id: "gest-calendario", icon: Calendar, label: "Calendario" },
            { id: "gest-centri", icon: Truck, label: "Gestione Centri" },
            { id: "gest-account", icon: Settings, label: "Account" },
          ].map((item) => {
            const I = item.icon;
            const active = section === item.id;
            return (
              <button
                key={item.id}
                data-testid={`admin-nav-${item.id}`}
                onClick={() => { setSection(item.id); setEditingCorso(null); setOpenCorso(null); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-semibold transition-colors ${active ? "bg-[#f59e0b] text-[#0a1020]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
              >
                <I className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1">
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-semibold text-slate-300 hover:bg-white/5">
            <ArrowLeft className="h-4 w-4" /> Area Soci
          </Link>
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-semibold text-slate-300 hover:bg-white/5">
            <LogOut className="h-4 w-4" /> Esci
          </Link>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-[#0e1424] text-white">
        <header className="sticky top-0 z-30 bg-[#0a1020]/85 backdrop-blur-md border-b border-white/5 px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-[#f59e0b]" />
            <h1 className="text-lg font-extrabold">Pannello Amministratore</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider font-bold text-[#f59e0b]">Admin</span>
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-[#0a1020] flex items-center justify-center font-bold text-[13px]">AD</div>
          </div>
        </header>

        <div className="p-8 space-y-6">
          {section === "dashboard" && <AdminDashboard onOpen={setSection} />}
          {section === "corsi" && (
            editingCorso ? (
              <CorsoEditor corso={editingCorso} onBack={() => setEditingCorso(null)} />
            ) : openCorso ? (
              <CorsoDetail corso={openCorso} onBack={() => setOpenCorso(null)} />
            ) : (
              <CorsiList onEdit={setEditingCorso} onOpen={setOpenCorso} />
            )
          )}
          {section === "iscritti" && <IscrittiSection />}
          {section === "vendite" && <AdminVenditeTab />}
          {section === "soci-anagrafica" && <SociAnagraficaSection />}
          {section === "corso-admin" && <CorsoAdminSection />}
          {section === "formazione-acdt" && <FormazioneAdminSection />}
          {section === "newsletter" && <NewsletterSection />}
          {section === "blog" && <BlogAdminSection />}
          {section === "eventi" && <EventiAdminSection />}
          {section === "partners" && <PartnersAdminSection />}
          {section === "approvazioni" && <AdminApprovazioniRegistro />}
          {section === "bonifici" && <BonificiSection />}
          {section === "spedizioni" && <SpedizioniSection />}
          {section === "registro" && <RegistroAdminSection />}
          {section === "quiz" && <QuizSection />}
          {section === "materiali" && <MaterialiSection />}
          {section === "abtest" && <AbTestSection />}
          {section === "reminder" && <ReminderEmailSection />}
          {section === "campagne" && <CampagneEmailSection />}
          {section === "gest-home" && <GestionaleHomeSection />}
          {section === "gest-soci" && <GestionalePlaceholder titolo="Soci" descrizione="Elenco Soci, Archivi, Gestione Avanzata, Cariche Sociali, Aggiungi Socio." link="soci-anagrafica" linkLabel="Vai ad Anagrafica Soci (già attiva)" />}
          {section === "gest-personale" && <GestionalePlaceholder titolo="Personale Esterno" descrizione="Gestione collaboratori non soci (formatori esterni, consulenti, freelance)." />}
          {section === "gest-affiliati" && <GestionalePlaceholder titolo="Affiliati" descrizione="Associazioni e enti aggregati. Gestione convenzioni." />}
          {section === "gest-amministrazione" && <GestionaleAmministrazione />}
          {section === "gest-tessere" && <GestionalePlaceholder titolo="Tessere" descrizione="Stampa tessere fisiche, gestione numerazione, scadenze annuali." link="soci-anagrafica" linkLabel="Vai ad Anagrafica (con numeri tessera)" />}
          {section === "gest-attivita" && <GestionalePlaceholder titolo="Attività" descrizione="Eventi formativi, workshop, seminari. Calendario attività associative." />}
          {section === "gest-documenti" && <GestionalePlaceholder titolo="Documenti" descrizione="Archivio documentale: statuto, atto, verbali consigli, deontologici." />}
          {section === "gest-formazione" && <GestionalePlaceholder titolo="Formazione" descrizione="Corsi attivi, CFDT erogati, attestati emessi." link="corso-admin" linkLabel="Vai a Corso A.C.D.T. (già attivo)" />}
          {section === "gest-calendario" && <GestionaleCalendario />}
          {section === "gest-centri" && <GestionalePlaceholder titolo="Gestione Centri" descrizione="Sedi territoriali, delegati regionali, coordinatori provinciali." />}
          {section === "gest-account" && <GestionalePlaceholder titolo="Account" descrizione="Utenti del pannello admin, permessi, log accessi." link="impostazioni" linkLabel="Vai a Impostazioni" />}
          {section === "impostazioni" && <ImpostazioniSection />}
        </div>
      </main>
    </div>
  );
}

// ----- DASHBOARD ADMIN -----
function AdminDashboard({ onOpen }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/stats/overview`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [API]);

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Caricamento statistiche…</div>;
  }
  if (!stats) {
    return <div className="text-center py-12 text-red-400">Impossibile caricare le statistiche.</div>;
  }

  const k = stats.kpi || {};
  const PIE_COLORS = { completato: "#22c55e", in_attesa: "#f59e0b", rifiutato: "#ef4444", sconosciuto: "#64748b" };

  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold">Panoramica generale</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">Statistiche aggregate live - Anno {stats.anno_corrente}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard data-testid="kpi-soci-totali" label="Soci totali" value={k.soci_totali} sub="esclusi admin" icon={Users} color="#3b82f6" />
        <KPICard data-testid="kpi-registro" label="Iscritti al Registro" value={k.iscritti_registro} sub="Registro Nazionale" icon={Award} color="#22c55e" />
        <KPICard data-testid="kpi-corso-base" label="Corso base completato" value={k.corso_base_completato} sub="con CFDT acquisiti" icon={GraduationCap} color="#f59e0b" />
        <KPICard data-testid="kpi-pagamenti-attesa" label="Bonifici in attesa" value={k.pagamenti_in_attesa} sub="da approvare" icon={Wallet} color="#ef4444" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Iscrizioni per mese" testid="chart-iscrizioni-mese">
          {stats.iscrizioni_mese.length === 0 ? (
            <EmptyChart label="Nessuna iscrizione registrata" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={stats.iscrizioni_mese} margin={{ top: 10, right: 12, left: -12, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="mese" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0a1020", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                <Line type="monotone" dataKey="iscritti" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: "#f59e0b" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Pagamenti per stato" testid="chart-pagamenti-stato">
          {stats.pagamenti_per_stato.length === 0 ? (
            <EmptyChart label="Nessun pagamento registrato" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.pagamenti_per_stato} dataKey="count" nameKey="stato" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.stato} (${e.count})`} labelLine={false}>
                  {stats.pagamenti_per_stato.map((p, i) => (
                    <Cell key={i} fill={PIE_COLORS[p.stato] || "#64748b"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1020", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} formatter={(value, name, props) => [`${value} pagamenti — €${props.payload.totale.toFixed(2)}`, props.payload.stato]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Distribuzione CFDT" testid="chart-cfdt">
          {stats.cfdt_distribuzione.length === 0 ? (
            <EmptyChart label="Nessun dato CFDT" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.cfdt_distribuzione} margin={{ top: 10, right: 12, left: -12, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="range" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0a1020", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Top 10 province" testid="chart-province">
          {stats.province.length === 0 ? (
            <EmptyChart label="Nessuna provincia dichiarata" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.province} layout="vertical" margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="provincia" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={50} />
                <Tooltip contentStyle={{ background: "#0a1020", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="soci" fill="#22c55e" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
        <h3 className="font-extrabold mb-4">Azioni rapide</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <QuickAction icon={Plus} label="Nuovo corso" onClick={() => onOpen("corsi")} accent />
          <QuickAction icon={Wallet} label="Approva bonifici" onClick={() => onOpen("bonifici")} />
          <QuickAction icon={Users} label="Vedi iscritti" onClick={() => onOpen("iscritti")} />
          <QuickAction icon={Upload} label="Carica materiale" onClick={() => onOpen("materiali")} />
        </div>
      </div>
    </>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <h3 className="font-extrabold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="h-[220px] flex items-center justify-center text-[12.5px] text-slate-500">{label}</div>;
}

function KPICard({ label, value, sub, icon: Icon, color, "data-testid": testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white/5 border border-white/10 p-5">
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
          <Icon className="h-5 w-5" />
        </div>
        <TrendingUp className="h-4 w-4 text-emerald-400" />
      </div>
      <div className="mt-3 text-[11.5px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-3xl font-extrabold">{value}</div>
      <div className="text-[12.5px] text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${accent ? "bg-[#f59e0b] text-[#0a1020] hover:bg-[#d97706]" : "bg-white/5 text-white hover:bg-white/10"}`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-bold text-[13.5px]">{label}</span>
    </button>
  );
}

// ----- LISTA CORSI -----
function CorsiList({ onEdit, onOpen }) {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">Gestione Corsi</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">{MOCK_ADMIN_CORSI.length} corsi totali</p>
        </div>
        <Button data-testid="btn-nuovo-corso" onClick={() => onEdit({ id: null, titolo: "", descrizione: "", prezzo: 0, ore: 0, cfdt: 0, stato: "bozza" })} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold">
          <Plus className="mr-2 h-4 w-4" /> Nuovo Corso
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Cerca corso..." className="pl-10 h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500" />
        </div>
        <Button variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white"><Filter className="mr-2 h-4 w-4" /> Filtra</Button>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead className="bg-white/5">
            <tr className="text-left text-[11.5px] uppercase tracking-wider text-slate-400">
              <th className="p-4">Corso</th>
              <th className="p-4">Stato</th>
              <th className="p-4">Prezzo</th>
              <th className="p-4">Ore / CFDT</th>
              <th className="p-4">Iscritti</th>
              <th className="p-4">Completamento</th>
              <th className="p-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_ADMIN_CORSI.map((c) => {
              const pct = c.iscritti > 0 ? Math.round((c.completati / c.iscritti) * 100) : 0;
              return (
                <tr key={c.id} data-testid={`admin-corso-row-${c.id}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="font-bold">{c.titolo}</div>
                    <div className="text-[11.5px] text-slate-400 mt-0.5">{c.lezioni} lezioni • {c.quiz} domande quiz</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider ${c.stato === "pubblicato" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                      {c.stato}
                    </span>
                  </td>
                  <td className="p-4 font-bold">{c.prezzo === 0 ? <span className="text-emerald-400">Gratis</span> : `€ ${c.prezzo.toFixed(2)}`}</td>
                  <td className="p-4">{c.ore}h / {c.cfdt} CFDT</td>
                  <td className="p-4 font-bold">{c.iscritti}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[100px] h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#f59e0b]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11.5px] font-bold">{pct}%</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => onOpen(c)} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center" title="Vedi"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => onEdit(c)} data-testid={`btn-edit-${c.id}`} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center" title="Modifica"><Edit3 className="h-4 w-4" /></button>
                      <button className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center" title="Elimina"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ----- EDITOR CORSO (creazione/modifica) -----
function CorsoEditor({ corso, onBack }) {
  const isNew = !corso.id;
  return (
    <>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#f59e0b] hover:underline mb-2">
        <ArrowLeft className="h-4 w-4" /> Torna ai corsi
      </button>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">{isNew ? "Nuovo Corso" : `Modifica: ${corso.titolo}`}</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">{isNew ? "Compila i dati per creare un nuovo corso" : "Aggiorna le informazioni del corso"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white" onClick={onBack}><X className="mr-2 h-4 w-4" /> Annulla</Button>
          <Button data-testid="btn-salva-corso" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"><Save className="mr-2 h-4 w-4" /> Salva</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Form principale */}
        <div className="lg:col-span-2 space-y-5">
          <Section title="Informazioni base">
            <Field label="Titolo del corso"><Input defaultValue={corso.titolo} placeholder="Es. Sostenibilità Digitale" className="bg-white/5 border-white/10 text-white" /></Field>
            <Field label="Descrizione"><Textarea defaultValue={corso.descrizione || ""} placeholder="Descrivi il corso, gli obiettivi e a chi è rivolto..." className="bg-white/5 border-white/10 text-white min-h-[100px]" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Ore"><Input type="number" defaultValue={corso.ore} className="bg-white/5 border-white/10 text-white" /></Field>
              <Field label="CFDT"><Input type="number" defaultValue={corso.cfdt} className="bg-white/5 border-white/10 text-white" /></Field>
              <Field label="Prezzo (€)"><Input type="number" defaultValue={corso.prezzo} className="bg-white/5 border-white/10 text-white" /></Field>
            </div>
          </Section>

          <Section title="Lezioni video">
            <div className="space-y-2">
              {[
                { t: "Modulo 1 - Introduzione", d: "32:14" },
                { t: "Modulo 2 - Strumenti", d: "45:08" },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="h-9 w-9 rounded-lg bg-[#102a6b] flex items-center justify-center text-white"><Video className="h-4 w-4" /></div>
                  <div className="flex-1">
                    <div className="font-bold text-[13.5px]">{l.t}</div>
                    <div className="text-[11px] text-slate-400">Durata: {l.d}</div>
                  </div>
                  <button className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center"><Edit3 className="h-4 w-4" /></button>
                  <button className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/15 text-slate-300 hover:bg-white/5 hover:text-white text-[13.5px] font-bold">
                <Plus className="h-4 w-4" /> Aggiungi lezione (carica video)
              </button>
            </div>
          </Section>

          <Section title="Materiali PDF">
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="h-9 w-9 rounded-lg bg-[#102a6b] flex items-center justify-center text-white"><FileText className="h-4 w-4" /></div>
                <div className="flex-1">
                  <div className="font-bold text-[13.5px]">Slide del corso</div>
                  <div className="text-[11px] text-slate-400">PDF • 2.4 MB</div>
                </div>
                <button className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
              <button className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/15 text-slate-300 hover:bg-white/5 hover:text-white text-[13.5px] font-bold">
                <Upload className="h-4 w-4" /> Carica nuovo PDF
              </button>
            </div>
          </Section>

          <Section title="Quiz finale">
            <div className="rounded-lg bg-[#f59e0b]/10 border border-[#f59e0b]/30 p-4 flex items-center gap-3">
              <FileQuestion className="h-5 w-5 text-[#f59e0b]" />
              <div className="flex-1 text-[13px] text-amber-200">Configura il quiz nella sezione <strong>Quiz & Test</strong> dopo aver salvato il corso.</div>
            </div>
          </Section>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <Section title="Pubblicazione">
            <Field label="Stato">
              <select defaultValue={corso.stato} className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-[14px]">
                <option value="bozza">Bozza</option>
                <option value="pubblicato">Pubblicato</option>
                <option value="archiviato">Archiviato</option>
              </select>
            </Field>
            <Field label="Categoria">
              <select className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-[14px]">
                <option>Base</option>
                <option>Sostenibilità</option>
                <option>AI & Innovazione</option>
                <option>Marketing</option>
              </select>
            </Field>
            <Field label="Livello">
              <select className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-[14px]">
                <option>Base</option>
                <option>Intermedio</option>
                <option>Avanzato</option>
              </select>
            </Field>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5">
              <input type="checkbox" id="premium" defaultChecked />
              <label htmlFor="premium" className="text-[13px]">Gratis per pacchetto Premium</label>
            </div>
          </Section>

          <Section title="Immagine di copertina">
            <div className="aspect-video rounded-lg border border-dashed border-white/15 bg-white/5 flex flex-col items-center justify-center text-slate-400">
              <Upload className="h-6 w-6 mb-2" />
              <div className="text-[12px] font-bold">Carica immagine</div>
              <div className="text-[10px] text-slate-500 mt-1">JPG / PNG • 16:9</div>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5 space-y-4">
      <h3 className="font-extrabold text-[15px]">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// ----- DETTAGLIO CORSO (visualizzazione) -----
function CorsoDetail({ corso, onBack }) {
  return (
    <>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#f59e0b] hover:underline mb-2">
        <ArrowLeft className="h-4 w-4" /> Torna ai corsi
      </button>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">{corso.titolo}</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Statistiche e iscritti del corso</p>
        </div>
        <Button className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"><Edit3 className="mr-2 h-4 w-4" /> Modifica</Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <KPICard label="Iscritti" value={corso.iscritti} sub="totali" icon={Users} color="#3b82f6" />
        <KPICard label="Completati" value={corso.completati} sub={`${Math.round((corso.completati / Math.max(corso.iscritti, 1)) * 100)}%`} icon={CheckCircle2} color="#22c55e" />
        <KPICard label="Lezioni" value={corso.lezioni} sub="video" icon={Video} color="#a78bfa" />
        <KPICard label="Quiz" value={corso.quiz} sub="domande" icon={FileQuestion} color="#f59e0b" />
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
        <h3 className="font-extrabold mb-4">Ultimi iscritti</h3>
        <IscrittiTable iscritti={MOCK_ADMIN_ISCRITTI.slice(0, 5)} compact />
      </div>
    </>
  );
}

// ----- ISCRITTI -----
function IscrittiSection() {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">Iscritti & Progressi</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Gestisci gli iscritti ai corsi e segna i completamenti</p>
        </div>
        <Button className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"><Award className="mr-2 h-4 w-4" /> Genera attestati</Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Cerca per nome, email o tessera..." className="pl-10 h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500" />
        </div>
        <select className="h-11 rounded-md bg-white/5 border border-white/10 text-white px-3 text-[14px]">
          <option>Tutti i corsi</option>
          {MOCK_ADMIN_CORSI.map(c => <option key={c.id}>{c.titolo}</option>)}
        </select>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <IscrittiTable iscritti={MOCK_ADMIN_ISCRITTI} />
      </div>
    </>
  );
}

function IscrittiTable({ iscritti, compact = false }) {
  return (
    <table className="w-full text-[13.5px]">
      <thead className={compact ? "" : "bg-white/5"}>
        <tr className="text-left text-[11.5px] uppercase tracking-wider text-slate-400">
          <th className="p-4">Socio</th>
          <th className="p-4">Tessera</th>
          <th className="p-4">Progresso</th>
          <th className="p-4">Stato</th>
          <th className="p-4">Iscrizione</th>
          <th className="p-4 text-right">Azioni</th>
        </tr>
      </thead>
      <tbody>
        {iscritti.map((u) => (
          <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
            <td className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#102a6b] to-[#1e40af] flex items-center justify-center text-[12px] font-bold">
                  {u.nome.split(" ").map(n => n[0]).join("").slice(0, 2)}
                </div>
                <div>
                  <div className="font-bold">{u.nome}</div>
                  <div className="text-[11.5px] text-slate-400">{u.email}</div>
                </div>
              </div>
            </td>
            <td className="p-4 font-mono text-[12px] text-slate-300">{u.tessera}</td>
            <td className="p-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 max-w-[100px] h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full ${u.completato ? "bg-emerald-500" : "bg-[#f59e0b]"}`} style={{ width: `${u.progresso}%` }} />
                </div>
                <span className="text-[11.5px] font-bold">{u.progresso}%</span>
              </div>
            </td>
            <td className="p-4">
              {u.completato ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-extrabold uppercase tracking-wider">
                  <CheckCircle2 className="h-3 w-3" /> Completato
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-[11px] font-extrabold uppercase tracking-wider">
                  <Clock className="h-3 w-3" /> In corso
                </span>
              )}
            </td>
            <td className="p-4 text-slate-400 text-[12px]">{new Date(u.dataIscrizione).toLocaleDateString("it-IT")}</td>
            <td className="p-4">
              <div className="flex items-center gap-1 justify-end">
                <button className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-300 text-[11.5px] font-bold hover:bg-emerald-500/25">{u.completato ? "Attestato" : "Completa"}</button>
                <button className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center"><Eye className="h-4 w-4" /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ----- QUIZ -----
function QuizSection() {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">Gestione Quiz & Test</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Crea domande, risposte multiple e configura i punteggi minimi</p>
        </div>
        <Button className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"><Plus className="mr-2 h-4 w-4" /> Nuova domanda</Button>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
        <div>
          <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">Corso di riferimento</Label>
          <select className="mt-1.5 w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-[14px]">
            {MOCK_ADMIN_CORSI.map(c => <option key={c.id}>{c.titolo}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {[
          { id: 1, q: "Quale dei seguenti è un principio fondamentale del GDPR?", n: 1 },
          { id: 2, q: "Il consulente A.C.D.T. deve aggiornare i CFDT ogni quanto?", n: 2 },
          { id: 3, q: "Cosa significa l'acronimo ESG?", n: 3 },
        ].map((q) => (
          <div key={q.id} className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#f59e0b] text-[#0a1020] flex items-center justify-center font-extrabold flex-shrink-0">Q{q.n}</div>
              <div className="flex-1">
                <div className="font-bold text-[14px]">{q.q}</div>
                <div className="mt-3 space-y-1.5">
                  {["Risposta A", "Risposta B (corretta)", "Risposta C", "Risposta D"].map((r, i) => (
                    <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] ${i === 1 ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-white/5 text-slate-300"}`}>
                      <span className="h-5 w-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold">{String.fromCharCode(65 + i)}</span>
                      {r}
                      {i === 1 && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center"><Edit3 className="h-4 w-4" /></button>
                <button className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ----- MATERIALI -----
function MaterialiSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [tipo, setTipo] = useState("materiale");
  const fileRef = useRef(null);

  const loadFiles = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/files`, { withCredentials: true })
      .then((r) => setFiles(r.data || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFiles(); }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipo);
    try {
      await axios.post(`${API}/api/admin/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      loadFiles();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante l'upload");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await axios.delete(`${API}/api/admin/files/${id}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.id !== id));
    } catch {
      setError("Errore durante l'eliminazione");
    }
  };

  const formatSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const iconForType = (ct = "") => {
    if (ct.startsWith("video/")) return Video;
    if (ct.startsWith("image/")) return Eye;
    return FileText;
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold">Materiali Didattici</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">PDF, slide, esercizi e documenti scaricabili — Object Storage Emergent</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="upload-tipo-select"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            disabled={uploading}
            className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] font-bold text-white"
          >
            <option value="materiale">Materiale</option>
            <option value="video">Video</option>
            <option value="immagine">Immagine</option>
          </select>
          <input
            ref={fileRef}
            type="file"
            data-testid="admin-upload-input"
            onChange={handleFileChange}
            disabled={uploading}
            accept="application/pdf,image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
            className="hidden"
          />
          <Button
            data-testid="admin-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold disabled:opacity-60"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? `Caricamento ${progress}%` : "Carica file"}
          </Button>
        </div>
      </div>

      {error && (
        <div data-testid="upload-error" className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-[13px] font-semibold p-3">
          {error}
        </div>
      )}

      {uploading && (
        <div className="rounded-lg bg-white/5 border border-white/10 p-3">
          <div className="text-[12px] text-slate-300 mb-2">Upload in corso… {progress}%</div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#f59e0b] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-[13px]">Caricamento file…</div>
      ) : files.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
          <Upload className="h-10 w-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-bold text-white">Nessun file caricato</h3>
          <p className="text-[13px] text-slate-400 mt-1">Clicca &quot;Carica file&quot; per caricare il primo materiale.</p>
        </div>
      ) : (
        <div data-testid="admin-files-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((m) => {
            const I = iconForType(m.content_type);
            return (
              <div key={m.id} data-testid={`admin-file-${m.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#102a6b] flex items-center justify-center"><I className="h-5 w-5 text-white" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] truncate" title={m.original_filename}>{m.original_filename}</div>
                    <div className="text-[11.5px] text-slate-400 mt-0.5 truncate uppercase">{m.tipo}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{m.content_type} • {formatSize(m.size)}</div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <a href={`${API}/api/files/${m.id}`} target="_blank" rel="noreferrer" className="flex-1 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-[12px] font-bold flex items-center justify-center gap-1">
                    <Eye className="h-3.5 w-3.5" /> Vedi
                  </a>
                  <button onClick={() => handleDelete(m.id)} data-testid={`delete-file-${m.id}`} className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}


// ----- BONIFICI / PAGAMENTI -----
function BonificiSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [filter, setFilter] = useState("in_attesa");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/payments${filter ? `?stato=${filter}` : ""}`, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const approve = async (p) => {
    if (!window.confirm(`Approvare bonifico di €${p.importo.toFixed(2)} da ${p.user_nome} ${p.user_cognome}?`)) return;
    setBusy(p.id);
    try {
      await axios.post(`${API}/api/admin/payments/${p.id}/approve`, {}, { withCredentials: true });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore approvazione");
    } finally { setBusy(null); }
  };

  const reject = async (p) => {
    const motivo = window.prompt("Motivo del rifiuto (obbligatorio):");
    if (!motivo || motivo.trim().length < 3) return;
    setBusy(p.id);
    try {
      await axios.post(`${API}/api/admin/payments/${p.id}/reject`, { motivo: motivo.trim() }, { withCredentials: true });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore rifiuto");
    } finally { setBusy(null); }
  };

  const STATI = [
    { id: "in_attesa", label: "In attesa", color: "text-[#f59e0b]" },
    { id: "completato", label: "Approvati", color: "text-emerald-400" },
    { id: "rifiutato", label: "Rifiutati", color: "text-red-400" },
    { id: "", label: "Tutti", color: "text-slate-300" },
  ];

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold">Bonifici & Pagamenti</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Approva o rifiuta i bonifici dichiarati dai soci</p>
        </div>
        <div className="flex gap-2">
          {STATI.map((s) => (
            <button
              key={s.id || "all"}
              data-testid={`filter-${s.id || "all"}`}
              onClick={() => setFilter(s.id)}
              className={`px-3 h-9 rounded-lg text-[12.5px] font-bold border transition-colors ${
                filter === s.id ? "bg-[#f59e0b] text-[#0a1020] border-[#f59e0b]" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-[13px] font-semibold p-3">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div data-testid="bonifici-empty" className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
          <Wallet className="h-10 w-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-bold">Nessun pagamento in questa vista</h3>
        </div>
      ) : (
        <div data-testid="bonifici-list" className="space-y-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`bonifico-${p.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10.5px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-white/10">{p.metodo}</span>
                    <span
                      className={`text-[10.5px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full ${
                        p.stato === "completato" ? "bg-emerald-500/15 text-emerald-400"
                        : p.stato === "rifiutato" ? "bg-red-500/15 text-red-400"
                        : "bg-[#f59e0b]/20 text-[#f59e0b]"
                      }`}
                    >
                      {p.stato === "in_attesa" ? "In attesa" : p.stato === "completato" ? "Approvato" : "Rifiutato"}
                    </span>
                  </div>
                  <div className="font-extrabold text-[15px]">{p.descrizione}</div>
                  <div className="text-[12px] text-slate-400 mt-1">
                    <span className="font-bold text-slate-200">{p.user_nome} {p.user_cognome}</span>
                    {" · "}{p.user_email}{p.user_tessera ? ` · ${p.user_tessera}` : ""}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Data: {p.data}{p.riferimento ? ` · Riferimento: ${p.riferimento}` : ""}
                  </div>
                  {p.note_admin && (
                    <div className="mt-2 text-[12px] text-slate-300 bg-white/5 border border-white/10 rounded-lg p-2">
                      <strong>Note admin:</strong> {p.note_admin}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-extrabold text-[#f59e0b]">€ {p.importo.toFixed(2)}</div>
                  {p.stato === "in_attesa" && (
                    <div className="mt-3 flex gap-2 justify-end">
                      <Button
                        data-testid={`approve-${p.id}`}
                        onClick={() => approve(p)}
                        disabled={busy === p.id}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-9 px-3 text-[12.5px]"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approva
                      </Button>
                      <Button
                        data-testid={`reject-${p.id}`}
                        onClick={() => reject(p)}
                        disabled={busy === p.id}
                        variant="outline"
                        className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-bold h-9 px-3 text-[12.5px]"
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Rifiuta
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


// ----- IMPOSTAZIONI -----
function ImpostazioniSection() {
  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold">Impostazioni</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">Configura i parametri della piattaforma</p>
      </div>

      <Section title="Crediti CFDT">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CFDT richiesti per rinnovo annuale"><Input type="number" defaultValue={20} className="bg-white/5 border-white/10 text-white" /></Field>
          <Field label="Ore = 1 CFDT"><Input type="number" defaultValue={1} className="bg-white/5 border-white/10 text-white" /></Field>
        </div>
      </Section>

      <Section title="Tesseramento">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Quota annuale base (€)"><Input type="number" defaultValue={50} className="bg-white/5 border-white/10 text-white" /></Field>
          <Field label="Quota Registro Nazionale (€)"><Input type="number" defaultValue={69.90} className="bg-white/5 border-white/10 text-white" /></Field>
        </div>
      </Section>

      <Section title="Quiz">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Punteggio minimo per superare (%)"><Input type="number" defaultValue={60} className="bg-white/5 border-white/10 text-white" /></Field>
          <Field label="Tentativi massimi per quiz"><Input type="number" defaultValue={3} className="bg-white/5 border-white/10 text-white" /></Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"><Save className="mr-2 h-4 w-4" /> Salva impostazioni</Button>
      </div>
    </>
  );
}


// ----- SPEDIZIONI MATERIALE PROFESSIONALE -----
function SpedizioniSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ stato: "spedita", tracking: "", corriere: "", note_admin: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/spedizioni${filter ? `?stato=${filter}` : ""}`, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      stato: s.stato === "richiesta" ? "spedita" : s.stato,
      tracking: s.tracking || "",
      corriere: s.corriere || "",
      note_admin: s.note_admin || "",
    });
    setError("");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await axios.put(`${API}/api/admin/spedizioni/${editing.id}`, editForm, { withCredentials: true });
      setEditing(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore aggiornamento spedizione");
    } finally { setSaving(false); }
  };

  const STATI = [
    { id: "richiesta", label: "Da spedire", color: "text-[#f59e0b]" },
    { id: "spedita", label: "Spedite", color: "text-blue-400" },
    { id: "consegnata", label: "Consegnate", color: "text-emerald-400" },
    { id: "", label: "Tutte", color: "text-slate-300" },
  ];

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold">Spedizioni Materiale Professionale</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Timbri rotondi + carta intestata per i soci iscritti al Registro</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={`${API}/api/admin/spedizioni/export.csv${filter ? `?stato=${filter}` : ""}`}
            data-testid="sped-export-csv"
            className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-[12.5px] font-bold bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
          >
            <Download className="h-4 w-4" /> CSV
          </a>
          <a
            href={`${API}/api/admin/spedizioni/etichette.pdf${filter ? `?stato=${filter}` : ""}`}
            target="_blank" rel="noreferrer"
            data-testid="sped-export-etichette"
            className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-[12.5px] font-bold bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-blue-500/25 transition-colors"
          >
            <Package className="h-4 w-4" /> Etichette PDF
          </a>
          {STATI.map((s) => (
            <button
              key={s.id || "all"}
              data-testid={`sped-filter-${s.id || "all"}`}
              onClick={() => setFilter(s.id)}
              className={`px-3 h-9 rounded-lg text-[12.5px] font-bold border transition-colors ${
                filter === s.id ? "bg-[#f59e0b] text-[#0a1020] border-[#f59e0b]" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div data-testid="spedizioni-empty" className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
          <Truck className="h-10 w-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-bold">Nessuna spedizione in questa vista</h3>
        </div>
      ) : (
        <div data-testid="spedizioni-list" className="space-y-3">
          {items.map((s) => {
            const badgeClass = s.stato === "consegnata" ? "bg-emerald-500/15 text-emerald-400"
              : s.stato === "spedita" ? "bg-blue-500/15 text-blue-300"
              : "bg-[#f59e0b]/20 text-[#f59e0b]";
            return (
              <div key={s.id} data-testid={`sped-${s.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="grid lg:grid-cols-[1fr_auto] gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`text-[10.5px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full ${badgeClass}`}>
                        {s.stato}
                      </span>
                      <span className="text-[11px] text-slate-400">richiesta il {new Date(s.created_at).toLocaleDateString("it-IT")}</span>
                    </div>
                    <div className="font-extrabold text-[15px]">{s.user_nome} {s.user_cognome}</div>
                    <div className="text-[12px] text-slate-400 mt-0.5">
                      {s.user_email}{s.user_tessera ? ` · ${s.user_tessera}` : ""}
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[13px]">
                      <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#f59e0b] mb-1">Indirizzo</div>
                        <div className="font-bold">{s.nome_destinatario}</div>
                        <div className="text-slate-300">{s.via} {s.civico}</div>
                        <div className="text-slate-300">{s.cap} {s.citta} ({s.provincia})</div>
                        <div className="text-slate-400 text-[12px] mt-1">Tel: {s.telefono}</div>
                      </div>
                      <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#f59e0b] mb-1">Spedizione</div>
                        {s.tracking ? (
                          <>
                            <div className="font-bold">{s.tracking}</div>
                            {s.corriere && <div className="text-slate-300">{s.corriere}</div>}
                          </>
                        ) : (
                          <div className="text-slate-500 text-[12px]">Nessun tracking ancora</div>
                        )}
                        {s.stato === "spedita" && s.spedita_il && <div className="text-slate-400 text-[11.5px] mt-1">Spedita il {new Date(s.spedita_il).toLocaleDateString("it-IT")}</div>}
                        {s.stato === "consegnata" && s.consegnata_il && <div className="text-slate-400 text-[11.5px]">Consegnata il {new Date(s.consegnata_il).toLocaleDateString("it-IT")}</div>}
                      </div>
                    </div>
                    {s.note && <div className="mt-2 text-[12px] text-slate-300 bg-white/5 border border-white/10 rounded-lg p-2"><strong>Note socio:</strong> {s.note}</div>}
                    {s.note_admin && <div className="mt-2 text-[12px] text-slate-300 bg-white/5 border border-white/10 rounded-lg p-2"><strong>Note admin:</strong> {s.note_admin}</div>}
                  </div>
                  <div className="flex lg:flex-col gap-2 lg:justify-start">
                    <Button
                      data-testid={`sped-gestisci-${s.id}`}
                      onClick={() => openEdit(s)}
                      className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold h-9 px-4"
                    >
                      <Package className="h-4 w-4 mr-1" /> Gestisci
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal aggiorna spedizione */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sped-edit-modal">
          <form onSubmit={submitEdit} className="bg-[#0a1020] border border-white/10 rounded-2xl p-6 max-w-lg w-full space-y-4 text-white">
            <h3 className="text-xl font-extrabold">Aggiorna spedizione</h3>
            <p className="text-[12.5px] text-slate-400 -mt-2">{editing.user_nome} {editing.user_cognome} · {editing.user_tessera}</p>

            <div>
              <Label className="text-[12.5px] font-semibold text-slate-200">Stato</Label>
              <select
                value={editForm.stato}
                onChange={(e) => setEditForm({ ...editForm, stato: e.target.value })}
                data-testid="sped-edit-stato"
                className="mt-1.5 w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-[14px] font-bold"
              >
                <option value="richiesta">Richiesta</option>
                <option value="spedita">Spedita</option>
                <option value="consegnata">Consegnata</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12.5px] font-semibold text-slate-200">Corriere</Label>
                <Input
                  data-testid="sped-edit-corriere"
                  value={editForm.corriere}
                  onChange={(e) => setEditForm({ ...editForm, corriere: e.target.value })}
                  placeholder="BRT, GLS, Poste…"
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-[12.5px] font-semibold text-slate-200">Tracking</Label>
                <Input
                  data-testid="sped-edit-tracking"
                  value={editForm.tracking}
                  onChange={(e) => setEditForm({ ...editForm, tracking: e.target.value })}
                  placeholder="Numero tracking"
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-[12.5px] font-semibold text-slate-200">Note al socio (opzionale)</Label>
              <Textarea
                data-testid="sped-edit-note"
                value={editForm.note_admin}
                onChange={(e) => setEditForm({ ...editForm, note_admin: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white/5 border-white/10 text-white"
                placeholder="Messaggio visibile al socio nella dashboard"
              />
            </div>

            {error && <div className="text-[13px] text-red-300 bg-red-500/15 border border-red-500/30 rounded-lg p-2">{error}</div>}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="border-white/10 bg-white/5 text-white hover:bg-white/10">
                Annulla
              </Button>
              <Button
                type="submit"
                data-testid="sped-edit-submit"
                disabled={saving}
                className="flex-1 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"
              >
                {saving ? "Salvataggio…" : "Salva"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}



// ----- ANAGRAFICA SOCI (admin editor onboarding) -----
function SociAnagraficaSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = (q = "") => {
    setLoading(true);
    const url = `${API}/api/admin/users${q ? `?search=${encodeURIComponent(q)}&role=user` : "?role=user"}`;
    axios.get(url, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold">Anagrafica Soci</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Visualizza e modifica i dati raccolti nell&apos;onboarding</p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="soci-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email, tessera, CF…"
              className="pl-10 h-10 rounded-lg bg-white/5 border border-white/10 text-[13px] text-white w-64"
            />
          </div>
          <Button type="submit" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold h-10">Cerca</Button>
        </form>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-bold">Nessun socio trovato</h3>
        </div>
      ) : (
        <div data-testid="soci-list" className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
                <th className="text-left p-3 font-bold">Tessera</th>
                <th className="text-left p-3 font-bold">Nome</th>
                <th className="text-left p-3 font-bold">Email</th>
                <th className="text-left p-3 font-bold">Pacchetto</th>
                <th className="text-left p-3 font-bold">Registro</th>
                <th className="text-left p-3 font-bold">Accesso</th>
                <th className="text-left p-3 font-bold">Onboarding</th>
                <th className="text-right p-3 font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`soc-row-${u.id}`} className="border-t border-white/10 hover:bg-white/5 transition-colors">
                  <td className="p-3 font-mono text-[11.5px]">{u.numero_tessera || "—"}</td>
                  <td className="p-3 font-bold">{u.nome} {u.cognome}</td>
                  <td className="p-3 text-slate-300">{u.email}</td>
                  <td className="p-3 capitalize text-[12px]">{u.pacchetto || "—"}</td>
                  <td className="p-3 text-[11.5px]">
                    {u.iscritto_registro ? <span className="text-emerald-400 font-bold">✓ Sì</span> : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="p-3 text-[11.5px]">
                    <button
                      data-testid={`soc-toggle-accesso-${u.id}`}
                      title={u.accesso_pieno ? "Disattiva accesso dashboard" : "Attiva manualmente (bonifico/contanti)"}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const next = !u.accesso_pieno;
                        const motivo = window.prompt(next
                          ? "Motivo attivazione manuale (es. bonifico ricevuto, contanti):"
                          : "Motivo disattivazione accesso:") || null;
                        if (motivo === null && !window.confirm(next ? "Attivare senza motivo?" : "Disattivare senza motivo?")) return;
                        try {
                          await axios.post(`${API}/api/admin/users/${u.id}/accesso-pieno`,
                            { attivo: next, motivo },
                            { withCredentials: true });
                          load(search);
                        } catch (err) { alert(err.response?.data?.detail || "Errore"); }
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-extrabold text-[10.5px] uppercase tracking-wider transition-colors ${u.accesso_pieno ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-red-500/15 text-red-300 hover:bg-red-500/25"}`}
                    >
                      <span className={`h-2 w-2 rounded-full ${u.accesso_pieno ? "bg-emerald-400" : "bg-red-400"}`} />
                      {u.accesso_pieno ? "Attivo" : "Bloccato"}
                    </button>
                  </td>
                  <td className="p-3 text-[11.5px]">
                    {u.onboarding_completato ? <span className="text-emerald-400 font-bold">✓ OK</span> : <span className="text-[#f59e0b] font-bold">in attesa</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        data-testid={`soc-toggle-registro-${u.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const next = !u.iscritto_registro;
                          const motivazione = next ? null : (window.prompt("Motivazione revoca (opzionale):") || null);
                          try {
                            await axios.post(`${API}/api/admin/users/${u.id}/registro`,
                              { iscritto_registro: next, motivazione },
                              { withCredentials: true });
                            load(search);
                          } catch (err) { alert(err.response?.data?.detail || "Errore"); }
                        }}
                        className={`h-8 px-2 text-[11px] ${u.iscritto_registro ? "bg-red-500/15 hover:bg-red-500/25 text-red-300" : "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"}`}
                        title={u.iscritto_registro ? "Revoca dal Registro" : "Approva al Registro"}
                      >
                        {u.iscritto_registro ? "Revoca Reg." : "Approva Reg."}
                      </Button>
                      <Button
                        data-testid={`soc-edit-${u.id}`}
                        onClick={() => setSelected(u)}
                        className="bg-white/10 hover:bg-white/20 text-white h-8 px-3 text-[12px]"
                      >
                        Apri
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <SocioEditorModal
          user={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setItems((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
            setSelected(updated);
          }}
          error={error}
          setError={setError}
        />
      )}
    </>
  );
}

function SocioEditorModal({ user, onClose, onSaved, error, setError }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    nome: user.nome || "", cognome: user.cognome || "", email: user.email || "",
    telefono: user.telefono || "",
    sesso: user.sesso || "",
    data_nascita: user.data_nascita || "",
    nazione_nascita: user.nazione_nascita || "",
    comune_nascita: user.comune_nascita || "",
    cap_nascita: user.cap_nascita || "",
    prov_nascita: user.prov_nascita || "",
    nazione_residenza: user.nazione_residenza || "",
    comune_residenza: user.comune_residenza || "",
    cap_residenza: user.cap_residenza || "",
    prov_residenza: user.prov_residenza || "",
    indirizzo_residenza: user.indirizzo_residenza || "",
    pacchetto: user.pacchetto || "",
    iscritto_registro: !!user.iscritto_registro,
    specializzazione: user.specializzazione || "",
    note_admin: user.note_admin || "",
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await axios.put(`${API}/api/admin/users/${user.id}`, form, { withCredentials: true });
      onSaved(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="soc-edit-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0a1020] border border-white/10 rounded-2xl max-w-3xl w-full text-white my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0a1020] z-10 p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-extrabold">{user.nome} {user.cognome}</h3>
            <div className="text-[11.5px] text-slate-400">{user.email} · {user.numero_tessera}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <SectionLabel>Account</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Nome" testid="ed-nome" value={form.nome} onChange={onChange("nome")} />
            <EF label="Cognome" testid="ed-cognome" value={form.cognome} onChange={onChange("cognome")} />
            <EF label="Email" testid="ed-email" value={form.email} onChange={onChange("email")} type="email" />
            <EF label="Telefono" testid="ed-telefono" value={form.telefono} onChange={onChange("telefono")} />
          </div>

          <SectionLabel>Dati anagrafici</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px] text-slate-300">Sesso</Label>
              <select value={form.sesso} onChange={onChange("sesso")} data-testid="ed-sesso" className="mt-1 w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white">
                <option value="">—</option><option value="M">M</option><option value="F">F</option>
              </select>
            </div>
            <EF label="Data di nascita" testid="ed-data-nascita" type="date" value={form.data_nascita} onChange={onChange("data_nascita")} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <EF label="Nazione nascita" testid="ed-naz-nascita" value={form.nazione_nascita} onChange={onChange("nazione_nascita")} />
            <EF label="Comune nascita" testid="ed-com-nascita" value={form.comune_nascita} onChange={onChange("comune_nascita")} />
            <div className="grid grid-cols-2 gap-2">
              <EF label="CAP" testid="ed-cap-nascita" value={form.cap_nascita} onChange={onChange("cap_nascita")} />
              <EF label="Prov" testid="ed-prov-nascita" value={form.prov_nascita} onChange={onChange("prov_nascita")} />
            </div>
          </div>

          <SectionLabel>Residenza</SectionLabel>
          <EF label="Indirizzo" testid="ed-indirizzo" value={form.indirizzo_residenza} onChange={onChange("indirizzo_residenza")} />
          <div className="grid grid-cols-3 gap-3">
            <EF label="Nazione" testid="ed-naz-res" value={form.nazione_residenza} onChange={onChange("nazione_residenza")} />
            <EF label="Comune" testid="ed-com-res" value={form.comune_residenza} onChange={onChange("comune_residenza")} />
            <div className="grid grid-cols-2 gap-2">
              <EF label="CAP" testid="ed-cap-res" value={form.cap_residenza} onChange={onChange("cap_residenza")} />
              <EF label="Prov" testid="ed-prov-res" value={form.prov_residenza} onChange={onChange("prov_residenza")} />
            </div>
          </div>

          <SectionLabel>Stato associativo</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px] text-slate-300">Pacchetto</Label>
              <select value={form.pacchetto} onChange={onChange("pacchetto")} data-testid="ed-pacchetto" className="mt-1 w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white">
                <option value="">—</option>
                <option value="free">Free</option>
                <option value="solo-corso">Solo corso</option>
                <option value="corso-tessera">Corso + Tessera</option>
                <option value="corso-tessera-registro">Corso + Tessera + Registro</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <EF label="Specializzazione" testid="ed-specializzazione" value={form.specializzazione} onChange={onChange("specializzazione")} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox" data-testid="ed-iscritto-registro"
              checked={form.iscritto_registro}
              onChange={(e) => setForm({ ...form, iscritto_registro: e.target.checked })}
              className="h-4 w-4 accent-[#f59e0b]"
            />
            <span className="text-[13px]">Iscritto al Registro Nazionale</span>
          </label>

          <div>
            <Label className="text-[12.5px] text-slate-300">Note interne (visibili solo admin)</Label>
            <Textarea data-testid="ed-note-admin" value={form.note_admin} onChange={onChange("note_admin")} rows={3} className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>

          {error && <div className="text-[13px] text-red-300 bg-red-500/15 border border-red-500/30 rounded-lg p-2">{error}</div>}
        </div>
        <div className="sticky bottom-0 bg-[#0a1020] border-t border-white/10 p-4 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} className="border-white/10 bg-white/5 text-white">Annulla</Button>
          <Button data-testid="ed-save" onClick={save} disabled={saving} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold px-6">
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#f59e0b] pt-2">{children}</div>;
}

function EF({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-[12.5px] text-slate-300">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={onChange} className="mt-1 bg-white/5 border-white/10 text-white" />
    </div>
  );
}



// ----- NEWSLETTER -----
function NewsletterSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({ oggetto: "", html: "", target: "all", pacchetto: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/newsletter`, { withCredentials: true })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (form.oggetto.length < 3 || form.html.length < 10) {
      setError("Oggetto min 3 caratteri, contenuto min 10 caratteri");
      return;
    }
    if (!window.confirm(`Inviare la newsletter al target "${form.target}"? Questa azione invierà email reali ai soci.`)) return;
    setSending(true); setError("");
    try {
      const payload = { ...form };
      if (payload.target !== "pacchetto") delete payload.pacchetto;
      const { data } = await axios.post(`${API}/api/admin/newsletter/send`, payload, { withCredentials: true });
      setResult(data);
      setForm({ oggetto: "", html: "", target: "all", pacchetto: "" });
      setTimeout(load, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const TARGETS = [
    { id: "all", label: "Tutti i soci" },
    { id: "premium", label: "Solo Premium" },
    { id: "iscritti_registro", label: "Iscritti al Registro" },
    { id: "pacchetto", label: "Pacchetto specifico…" },
  ];

  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold">Newsletter</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">Invia comunicazioni ai soci selezionati. Powered by Resend.</p>
      </div>

      {!process.env.RESEND_INFO_HIDDEN && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[12.5px] p-4">
          <strong>Suggerimento:</strong> assicurati che <code className="bg-black/30 px-1.5 rounded">RESEND_API_KEY</code> sia configurata in backend/.env per attivare l&apos;invio reale. Senza la chiave la newsletter viene tracciata ma le email NON vengono spedite.
        </div>
      )}

      <form onSubmit={handleSend} data-testid="newsletter-form" className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
        <div>
          <Label className="text-[12.5px] text-slate-200">Oggetto</Label>
          <Input data-testid="nl-oggetto" value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
            placeholder="Es. Aggiornamento crediti CFDT 2026"
            className="mt-1 bg-white/5 border-white/10 text-white" />
        </div>

        <div>
          <Label className="text-[12.5px] text-slate-200">Contenuto HTML</Label>
          <Textarea data-testid="nl-html" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })}
            rows={8}
            placeholder="<p>Ciao soci,</p><p>Vi informiamo che…</p>"
            className="mt-1 bg-white/5 border-white/10 text-white font-mono text-[12.5px]" />
          <div className="mt-1 text-[11px] text-slate-400">HTML semplice supportato. Verrà avvolto nel template branding A.C.D.T.</div>
        </div>

        <div>
          <Label className="text-[12.5px] text-slate-200">Destinatari</Label>
          <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {TARGETS.map((t) => (
              <button key={t.id} type="button" data-testid={`nl-target-${t.id}`}
                onClick={() => setForm({ ...form, target: t.id })}
                className={`h-10 px-3 rounded-lg text-[12px] font-bold border transition-colors ${
                  form.target === t.id ? "bg-[#f59e0b] text-[#0a1020] border-[#f59e0b]" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          {form.target === "pacchetto" && (
            <Input data-testid="nl-pacchetto" placeholder="es. corso-tessera"
              value={form.pacchetto} onChange={(e) => setForm({ ...form, pacchetto: e.target.value })}
              className="mt-2 bg-white/5 border-white/10 text-white" />
          )}
        </div>

        {error && <div data-testid="nl-error" className="text-[13px] text-red-300 bg-red-500/15 border border-red-500/30 rounded-lg p-2">{error}</div>}
        {result && (
          <div data-testid="nl-result" className="text-[13px] text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-lg p-3">
            ✓ Newsletter <strong>{result.newsletter_id?.slice(0, 8)}</strong> avviata. Destinatari: {result.destinatari}. Stato: {result.stato}.
            {result.warning && <div className="text-amber-300 mt-1">{result.warning}</div>}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" data-testid="nl-send"
            disabled={sending}
            className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold h-11 px-7">
            {sending ? "Invio…" : (<><Send className="mr-2 h-4 w-4" /> Invia newsletter</>)}
          </Button>
        </div>
      </form>

      <div>
        <h3 className="text-lg font-extrabold mt-2">Storico invii</h3>
        {loading ? (
          <div className="text-center py-6 text-slate-400">Caricamento…</div>
        ) : history.length === 0 ? (
          <div className="text-[13px] text-slate-500 py-6 text-center">Nessuna newsletter inviata.</div>
        ) : (
          <div data-testid="nl-history" className="space-y-2 mt-3">
            {history.map((n) => (
              <div key={n.id} className="rounded-xl bg-white/5 border border-white/10 p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-[13.5px] truncate">{n.oggetto}</div>
                  <div className="text-[11px] text-slate-400">
                    {new Date(n.created_at).toLocaleString("it-IT")} · target: {n.target}{n.pacchetto ? ` (${n.pacchetto})` : ""}
                  </div>
                </div>
                <div className="text-right text-[11.5px]">
                  <div className="font-bold">{n.inviati ?? 0} / {n.destinatari_totali}</div>
                  <div className={`text-[10px] uppercase font-extrabold ${
                    n.stato === "completata" ? "text-emerald-400" : "text-[#f59e0b]"
                  }`}>{n.stato}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}



// ----- CORSO ABILITAZIONE — gestione moduli + video + analytics download -----
function CorsoAdminSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [materiali, setMateriali] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/corso-abilitazione/materiali`, { withCredentials: true }),
      axios.get(`${API}/api/admin/corso-abilitazione/analytics`, { withCredentials: true }),
    ]).then(([m, a]) => {
      setMateriali(m.data || []);
      setAnalytics(a.data || null);
    }).catch(() => setError("Errore caricamento")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const editVideo = async (m) => {
    const url = window.prompt(
      `Imposta URL embed video per "${m.titolo}".\nFormato: https://www.youtube.com/embed/VIDEOID o https://player.vimeo.com/video/VIDEOID\n(vuoto per rimuovere)`,
      m.video_url || "",
    );
    if (url === null) return;
    try {
      await axios.put(`${API}/api/admin/corso-abilitazione/materiali/${m.id}`, { video_url: url || "" }, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const togglePub = async (m) => {
    try {
      await axios.put(`${API}/api/admin/corso-abilitazione/materiali/${m.id}`, { accesso_pubblico: !m.accesso_pubblico }, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const delMat = async (m) => {
    if (!window.confirm(`Eliminare "${m.titolo}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/corso-abilitazione/materiali/${m.id}`, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold">Corso A.C.D.T. per la qualificazione professionale — Gestione</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">Materiali PDF, video moduli e analytics download</p>
      </div>

      {error && <div className="text-[13px] text-red-300 bg-red-500/15 border border-red-500/30 rounded-lg p-2">{error}</div>}

      {/* KPI Analytics */}
      {analytics && (
        <div data-testid="corso-analytics-kpi" className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Download totali</div>
            <div className="text-3xl font-extrabold text-[#f59e0b] mt-1">{analytics.totale_downloads}</div>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Materiali pubblicati</div>
            <div className="text-3xl font-extrabold text-white mt-1">{materiali.length}</div>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Moduli con video</div>
            <div className="text-3xl font-extrabold text-emerald-400 mt-1">{materiali.filter(m => m.video_url).length}</div>
          </div>
        </div>
      )}

      {/* Lista materiali */}
      <div>
        <h3 className="font-extrabold text-lg mb-3">Materiali</h3>
        {loading ? (
          <div className="text-slate-400 text-center py-6">Caricamento…</div>
        ) : (
          <div data-testid="admin-materiali-list" className="space-y-2">
            {materiali.map((m) => (
              <div key={m.id} data-testid={`admin-mat-${m.id}`} className="rounded-xl bg-white/5 border border-white/10 p-4 flex items-center gap-3 flex-wrap">
                <div className="h-10 w-10 rounded-lg bg-[#102a6b] text-white font-extrabold flex items-center justify-center flex-shrink-0">
                  {m.modulo_n ? String(m.modulo_n).padStart(2, "0") : "✦"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[13.5px] truncate">{m.titolo}</div>
                  <div className="text-[11px] text-slate-400">
                    {(m.size / 1024 / 1024).toFixed(1)} MB · {m.downloads_count || 0} download
                    {m.accesso_pubblico && " · 🌐 Pubblico"}
                    {m.video_url && " · ▶ Video attivo"}
                  </div>
                </div>
                <Button data-testid={`mat-video-${m.id}`} onClick={() => editVideo(m)}
                  className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white h-8 px-3 text-[11.5px]">
                  {m.video_url ? "Modifica video" : "Imposta video"}
                </Button>
                <Button onClick={() => togglePub(m)}
                  className={`h-8 px-3 text-[11.5px] ${m.accesso_pubblico ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-slate-300"}`}>
                  {m.accesso_pubblico ? "Pubblico ✓" : "Solo corso"}
                </Button>
                <Button onClick={() => delMat(m)} className="bg-red-500/15 hover:bg-red-500/25 text-red-300 h-8 px-3 text-[11.5px]">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Storico download */}
      {analytics?.recent?.length > 0 && (
        <div>
          <h3 className="font-extrabold text-lg mb-3">Ultimi download</h3>
          <div data-testid="download-recent" className="overflow-x-auto rounded-2xl bg-white/5 border border-white/10">
            <table className="w-full text-[12.5px]">
              <thead><tr className="bg-white/5 text-[10.5px] uppercase tracking-wider text-slate-400">
                <th className="text-left p-3">Data</th><th className="text-left p-3">Socio</th>
                <th className="text-left p-3">Tessera</th><th className="text-left p-3">Materiale</th>
              </tr></thead>
              <tbody>
                {analytics.recent.slice(0, 30).map((r, i) => (
                  <tr key={i} className="border-t border-white/10">
                    <td className="p-3 text-slate-400">{new Date(r.downloaded_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="p-3 font-bold">{r.user_nome}</td>
                    <td className="p-3 text-slate-400 font-mono text-[11px]">{r.user_tessera || "—"}</td>
                    <td className="p-3 text-slate-300">{r.modulo_n ? `Mod ${r.modulo_n} — ` : ""}{r.materiale_titolo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}


// ============================================================================
// A/B Test CTA — dashboard metriche
// ============================================================================
function AbTestSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/ab/quick-stats-cta/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      setStats({ error: e.response?.data?.detail || "Errore caricamento" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-slate-400 text-sm">Caricamento metriche...</div>;
  if (!stats || stats.error) return <div className="text-red-400 text-sm">{stats?.error || "Nessun dato"}</div>;

  const variants = {
    A: { label: "Unisciti a noi →", color: "#f59e0b" },
    B: { label: "Entra nel Registro →", color: "#10b981" },
  };

  return (
    <div data-testid="ab-test-section" className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold">A/B Test — CTA Quick Stats Bar</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">
          Variante mostrata casualmente (50/50) ai visitatori della home page. Sticky per sessione.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Impressioni totali</div>
          <div className="text-3xl font-extrabold text-white mt-1" data-testid="ab-total-impressions">{stats.totale_impressioni}</div>
        </div>
        <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Click totali</div>
          <div className="text-3xl font-extrabold text-white mt-1" data-testid="ab-total-clicks">{stats.totale_click}</div>
        </div>
        <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Conversion globale</div>
          <div className="text-3xl font-extrabold text-[#f59e0b] mt-1" data-testid="ab-conversion-global">{stats.conversion_globale}%</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {stats.per_variante.map((v) => {
          const cfg = variants[v.variant] || { label: v.variant, color: "#94a3b8" };
          const isWinner = stats.winner === v.variant;
          return (
            <div
              key={v.variant}
              data-testid={`ab-variant-${v.variant}`}
              className={`bg-[#0a1020] rounded-xl p-5 border ${isWinner ? "border-green-500 ring-2 ring-green-500/30" : "border-white/5"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Variante {v.variant}</div>
                  <div className="text-lg font-extrabold mt-1" style={{ color: cfg.color }}>{cfg.label}</div>
                </div>
                {isWinner && (
                  <span data-testid={`ab-winner-${v.variant}`} className="text-[10px] uppercase tracking-wider font-extrabold bg-green-500 text-[#0a1020] px-2 py-1 rounded">
                    Winner
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Impr.</div>
                  <div className="text-xl font-bold">{v.impressioni}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Click</div>
                  <div className="text-xl font-bold">{v.click}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CR</div>
                  <div className="text-xl font-bold" style={{ color: cfg.color }}>{v.conversion_rate}%</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[12px] text-slate-500">
        Significatività raggiunta solo dopo {stats.soglia_significatività}. Stato attuale: {stats.winner ? `winner ${stats.winner}` : "raccolta dati in corso..."}.
      </div>

      <Button data-testid="ab-refresh" onClick={load} variant="outline" className="border-white/10 text-white hover:bg-white/5">
        Aggiorna
      </Button>
    </div>
  );
}


// ============================================================================
// Reminder Email — trigger manuale + ultima esecuzione
// ============================================================================
function ReminderEmailSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [runningScad, setRunningScad] = useState(false);
  const [lastResultScad, setLastResultScad] = useState(null);
  const [errorScad, setErrorScad] = useState(null);
  const [runningGuest, setRunningGuest] = useState(false);
  const [lastResultGuest, setLastResultGuest] = useState(null);
  const [errorGuest, setErrorGuest] = useState(null);

  const runReminder = async () => {
    setRunning(true); setError(null); setLastResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/reminder-quiz/run`, {}, { withCredentials: true });
      setLastResult({ ...r.data, at: new Date().toLocaleString("it-IT") });
    } catch (e) { setError(e.response?.data?.detail || "Errore esecuzione"); }
    finally { setRunning(false); }
  };

  const runScadenza = async () => {
    setRunningScad(true); setErrorScad(null); setLastResultScad(null);
    try {
      const r = await axios.post(`${API}/api/admin/reminder-scadenza/run`, {}, { withCredentials: true });
      setLastResultScad({ ...r.data, at: new Date().toLocaleString("it-IT") });
    } catch (e) { setErrorScad(e.response?.data?.detail || "Errore esecuzione"); }
    finally { setRunningScad(false); }
  };

  const runGuest = async () => {
    setRunningGuest(true); setErrorGuest(null); setLastResultGuest(null);
    try {
      const r = await axios.post(`${API}/api/admin/reminder-guest/run`, {}, { withCredentials: true });
      setLastResultGuest({ ...r.data, at: new Date().toLocaleString("it-IT") });
    } catch (e) { setErrorGuest(e.response?.data?.detail || "Errore esecuzione"); }
    finally { setRunningGuest(false); }
  };

  return (
    <div data-testid="reminder-email-section" className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold">Reminder Email Automatici</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">
          Job giornalieri che inviano promemoria ai soci. Tutti i job vengono eseguiti automaticamente — i pulsanti qui sotto servono solo per i test.
        </p>
      </div>

      {/* JOB 1: Reminder Quiz */}
      <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5 space-y-3">
        <h3 className="font-extrabold text-lg">📝 Reminder Quiz Finale</h3>
        <ul className="text-[13px] text-slate-300 space-y-1">
          <li>• <strong>Trigger</strong>: ogni giorno alle <strong>09:00</strong> (Europe/Rome)</li>
          <li>• <strong>Condizione</strong>: corso iniziato + quiz non superato + ultima attività ≥ <strong>7 giorni</strong> fa</li>
          <li>• <strong>Frequenza</strong>: 1 unica email per iscrizione (no spam)</li>
          <li>• <strong>Mittente</strong>: noreply@assoacdt.com</li>
        </ul>

        <div className="pt-2 flex items-center gap-3">
          <Button
            data-testid="reminder-run-btn"
            onClick={runReminder}
            disabled={running}
            className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold"
          >
            {running ? "Esecuzione..." : "Esegui ora (test)"}
          </Button>
          <span className="text-[12px] text-slate-500">Utile per validare il flusso senza aspettare le 09:00.</span>
        </div>

        {error && <div className="text-red-400 text-sm" data-testid="reminder-error">❌ {error}</div>}

        {lastResult && (
          <div className="bg-[#0e1424] rounded-lg p-4 border border-white/5 mt-3" data-testid="reminder-result">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">
              Ultima esecuzione · {lastResult.at}
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Candidati</div>
                <div className="text-xl font-bold">{lastResult.candidati}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Inviati</div>
                <div className="text-xl font-bold text-green-400" data-testid="reminder-sent-count">{lastResult.inviati}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Skippati</div>
                <div className="text-xl font-bold text-slate-400">{lastResult.skippati}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Errori</div>
                <div className={`text-xl font-bold ${lastResult.errori > 0 ? "text-red-400" : "text-slate-400"}`}>{lastResult.errori}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* JOB 2: Reminder Scadenza Tessera */}
      <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5 space-y-3">
        <h3 className="font-extrabold text-lg">⏰ Reminder Tessera in Scadenza</h3>
        <ul className="text-[13px] text-slate-300 space-y-1">
          <li>• <strong>Trigger</strong>: ogni giorno alle <strong>09:05</strong> (Europe/Rome)</li>
          <li>• <strong>Soglie invio</strong>: 30 giorni, 15 giorni e 3 giorni prima della scadenza</li>
          <li>• <strong>Frequenza</strong>: 1 email per soglia (max 3 reminder totali per ciclo annuale)</li>
          <li>• <strong>Subject differenziato</strong>: standard (30gg) → ⏰ (15gg) → 🔴 URGENTE (3gg)</li>
          <li>• <strong>Mittente</strong>: noreply@assoacdt.com</li>
        </ul>

        <div className="pt-2 flex items-center gap-3">
          <Button
            data-testid="reminder-scadenza-run-btn"
            onClick={runScadenza}
            disabled={runningScad}
            className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold"
          >
            {runningScad ? "Esecuzione..." : "Esegui ora (test)"}
          </Button>
          <span className="text-[12px] text-slate-500">Scansione 30/15/3 giorni residui.</span>
        </div>

        {errorScad && <div className="text-red-400 text-sm" data-testid="reminder-scadenza-error">❌ {errorScad}</div>}

        {lastResultScad && (
          <div className="bg-[#0e1424] rounded-lg p-4 border border-white/5 mt-3" data-testid="reminder-scadenza-result">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">
              Ultima esecuzione · {lastResultScad.at}
            </div>
            <div className="grid grid-cols-5 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Candidati</div>
                <div className="text-xl font-bold">{lastResultScad.candidati}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Tot. inviati</div>
                <div className="text-xl font-bold text-green-400">{lastResultScad.inviati_totali}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">30gg</div>
                <div className="text-xl font-bold">{lastResultScad.inviati_per_soglia?.["30d"] ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">15gg</div>
                <div className="text-xl font-bold">{lastResultScad.inviati_per_soglia?.["15d"] ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">3gg</div>
                <div className="text-xl font-bold text-red-400">{lastResultScad.inviati_per_soglia?.["3d"] ?? 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CAMPAGNE EMAIL OUTREACH
// ============================================================================
const DEFAULT_TEMPLATE = `<p>Ciao <strong>{{nome}}</strong>,</p>

<p>Ti scriviamo da <strong>A.C.D.T.</strong> (Associazione Consulenti Digitali Tecnologici),
l'associazione professionale italiana che dal 2024 promuove la formazione certificata
dei <strong>Consulenti e Consumatori Digitali, Tecnologici e di Sostenibilità</strong>.</p>

<p>Siamo aggregati al <strong>MIMIT</strong> tramite Centro Studi E.S.S.E. e ai sensi
della <strong>Legge 4/2013</strong> rilasciamo:</p>
<ul>
  <li>Tessera digitale + QR code di verifica pubblica</li>
  <li>Iscrizione al Registro Nazionale</li>
  <li>Corsi accreditati con crediti CFDT</li>
  <li>Timbro rotondo e carta intestata ufficiale</li>
</ul>

<p>Se ti interessa scoprire come la nostra associazione può aiutarti a
<strong>professionalizzare la tua attività digitale</strong>, clicca il pulsante qui sotto.</p>

<p>Un caro saluto,<br/><strong>La segreteria A.C.D.T.</strong></p>`;


function CampagneEmailSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [view, setView] = useState("list"); // list | new | detail
  const [campaigns, setCampaigns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/campaigns`, { withCredentials: true });
      setCampaigns(r.data || []);
    } catch (e) {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCampaigns(); }, []);

  if (view === "new") {
    return <CampagnaNuova onDone={(id) => { loadCampaigns(); if (id) { setActiveId(id); setView("detail"); } else { setView("list"); } }} onCancel={() => setView("list")} />;
  }
  if (view === "detail" && activeId) {
    return <CampagnaDetail campaignId={activeId} onBack={() => { setActiveId(null); setView("list"); loadCampaigns(); }} />;
  }

  return (
    <div data-testid="campagne-email-section" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-extrabold">Campagne Email Outreach</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">
            Carica un CSV con contatti e invia un&apos;email di presentazione dell&apos;associazione.
            Include automaticamente link di annullamento iscrizione (GDPR).
          </p>
        </div>
        <Button
          data-testid="campagna-nuova-btn"
          onClick={() => setView("new")}
          className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold"
        >
          <Plus className="h-4 w-4 mr-1" /> Nuova campagna
        </Button>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-[13px] leading-relaxed">
        <strong>⚠️ Ricorda</strong>: invia email solo a contatti che hanno dato il consenso GDPR
        (es. iscritti newsletter, contatti acquisiti tramite form con checkbox).
        L&apos;invio massivo a contatti &quot;a freddo&quot; può comportare sanzioni del Garante della Privacy.
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Caricamento...</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 bg-[#0a1020] rounded-xl border border-white/5">
          <Mail className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nessuna campagna creata. Clicca &quot;Nuova campagna&quot; per iniziare.</p>
        </div>
      ) : (
        <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#0e1424] text-slate-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Nome</th>
                <th className="text-left px-4 py-3 font-bold">Subject</th>
                <th className="text-center px-4 py-3 font-bold">Stato</th>
                <th className="text-center px-4 py-3 font-bold">Tot.</th>
                <th className="text-center px-4 py-3 font-bold">Inviati</th>
                <th className="text-center px-4 py-3 font-bold">Falliti</th>
                <th className="text-left px-4 py-3 font-bold">Creata</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const statusColors = {
                  bozza: "bg-slate-500/20 text-slate-300",
                  in_corso: "bg-blue-500/20 text-blue-300 animate-pulse",
                  completata: "bg-green-500/20 text-green-400",
                };
                return (
                  <tr key={c.id} data-testid={`campagna-row-${c.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-semibold">{c.nome}</td>
                    <td className="px-4 py-3 text-slate-300 truncate max-w-xs">{c.subject}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] uppercase tracking-wider font-extrabold px-2 py-1 rounded ${statusColors[c.status] || "bg-slate-500/20 text-slate-300"}`}>
                        {c.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-bold">{c.total}</td>
                    <td className="px-4 py-3 text-center font-bold text-green-400">{c.sent}</td>
                    <td className="px-4 py-3 text-center font-bold text-red-400">{c.failed}</td>
                    <td className="px-4 py-3 text-slate-400 text-[12px]">{new Date(c.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`campagna-open-${c.id}`}
                        onClick={() => { setActiveId(c.id); setView("detail"); }}
                        className="text-[12px] text-[#fbbf24] hover:text-white font-bold"
                      >
                        Apri →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CampagnaNuova({ onDone, onCancel }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [step, setStep] = useState(1); // 1 contatti, 2 contenuto, 3 conferma
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [nome, setNome] = useState("");
  const [subject, setSubject] = useState("Scopri A.C.D.T. — Associazione Consulenti Digitali");
  const [htmlBody, setHtmlBody] = useState(DEFAULT_TEMPLATE);
  const [ctaUrl, setCtaUrl] = useState("https://assoacdt.com");
  const [ctaLabel, setCtaLabel] = useState("Scopri A.C.D.T.");
  const [gdprConfirmed, setGdprConfirmed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadError(null);
    setPreview(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns/preview`, fd, { withCredentials: true });
      setPreview(r.data);
    } catch (err) {
      setUploadError(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    const r = await axios.get(`${API}/api/admin/campaigns/template-csv`, { withCredentials: true, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = "acdt-contatti-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const createCampaign = async (autoSend = false) => {
    setCreating(true);
    setCreateError(null);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns`, {
        nome: nome.trim(),
        subject: subject.trim(),
        html_body: htmlBody,
        cta_url: ctaUrl.trim(),
        cta_label: ctaLabel.trim(),
        gdpr_consent_confirmed: gdprConfirmed,
        contacts: preview.valid_contacts,
      }, { withCredentials: true });
      const cid = r.data.campaign_id;
      if (autoSend) {
        await axios.post(`${API}/api/admin/campaigns/${cid}/send`, {}, { withCredentials: true });
      }
      onDone(cid);
    } catch (err) {
      setCreateError(err.response?.data?.detail || "Errore creazione");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="campagna-nuova" className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-[12px] text-slate-400 hover:text-white flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Indietro
        </button>
        <h2 className="text-2xl font-extrabold">Nuova Campagna Email</h2>
        <div className="w-20"></div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 text-[12px]">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center font-extrabold ${step >= s ? "bg-[#f59e0b] text-[#0a1020]" : "bg-[#0a1020] text-slate-500"}`}>
              {s}
            </div>
            {s < 3 && <div className={`h-0.5 w-12 ${step > s ? "bg-[#f59e0b]" : "bg-[#0a1020]"}`}></div>}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Contatti */}
      {step === 1 && (
        <div className="space-y-4 max-w-4xl mx-auto" data-testid="step-contatti">
          <h3 className="font-extrabold text-lg">1. Carica i contatti</h3>
          <p className="text-[13px] text-slate-400">
            File CSV con colonne <code className="text-[#fbbf24]">email, nome, cognome</code>.
            L&apos;email è obbligatoria, le altre sono opzionali.
          </p>

          <div className="flex gap-2">
            <Button onClick={downloadTemplate} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-1" /> Scarica template CSV
            </Button>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] rounded-md font-extrabold text-[13px] cursor-pointer transition-colors">
              <Upload className="h-4 w-4" />
              {uploading ? "Caricamento..." : "Carica CSV"}
              <input type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid="csv-upload-input" />
            </label>
          </div>

          {uploadError && <div className="text-red-400 text-sm" data-testid="upload-error">❌ {uploadError}</div>}

          {preview && (
            <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5 space-y-3" data-testid="preview-result">
              <h4 className="font-extrabold">Risultato analisi CSV</h4>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Caricati</div>
                  <div className="text-2xl font-bold">{preview.total_caricati}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Validi</div>
                  <div className="text-2xl font-bold text-green-400" data-testid="preview-valid">{preview.valid_contacts.length}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Duplicati esclusi</div>
                  <div className="text-2xl font-bold text-slate-400">{preview.duplicates_removed}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Unsub esclusi</div>
                  <div className="text-2xl font-bold text-amber-400">{preview.unsubscribed_excluded}</div>
                </div>
              </div>

              {preview.invalid_rows.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-red-400 font-bold">{preview.invalid_rows.length} righe scartate</summary>
                  <ul className="mt-2 pl-4 text-slate-400 space-y-1">
                    {preview.invalid_rows.slice(0, 10).map((r, i) => (
                      <li key={i}>Riga {r.riga}: {r.email || "(vuota)"} — {r.motivo}</li>
                    ))}
                  </ul>
                </details>
              )}

              {preview.valid_contacts.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-slate-300 font-bold">Anteprima primi 5 contatti validi</summary>
                  <ul className="mt-2 pl-4 text-slate-400 space-y-1">
                    {preview.valid_contacts.slice(0, 5).map((c, i) => (
                      <li key={i}>{c.email} {c.nome && `— ${c.nome} ${c.cognome}`}</li>
                    ))}
                    {preview.valid_contacts.length > 5 && <li className="italic">... e altri {preview.valid_contacts.length - 5}</li>}
                  </ul>
                </details>
              )}
            </div>
          )}

          {preview && preview.valid_contacts.length > 0 && (
            <Button onClick={() => setStep(2)} className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold" data-testid="next-step-2">
              Avanti → Scrivi l&apos;email
            </Button>
          )}
        </div>
      )}

      {/* STEP 2: Contenuto */}
      {step === 2 && (
        <div className="space-y-4 max-w-4xl mx-auto" data-testid="step-contenuto">
          <h3 className="font-extrabold text-lg">2. Scrivi l&apos;email</h3>
          <p className="text-[13px] text-slate-400">
            Usa <code className="text-[#fbbf24]">{`{{nome}}`}</code> per personalizzare con il nome del contatto.
            Il template include automaticamente header A.C.D.T., CTA e footer GDPR.
          </p>

          <div className="space-y-3">
            <div>
              <Label className="text-[12px] uppercase tracking-wider font-bold text-slate-400">Nome interno campagna</Label>
              <Input
                data-testid="input-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Es. Campagna primavera 2026"
                className="bg-[#0a1020] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-[12px] uppercase tracking-wider font-bold text-slate-400">Oggetto email</Label>
              <Input
                data-testid="input-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="bg-[#0a1020] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-[12px] uppercase tracking-wider font-bold text-slate-400">Corpo email (HTML)</Label>
              <Textarea
                data-testid="input-html-body"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={14}
                className="bg-[#0a1020] border-white/10 text-white font-mono text-[12px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] uppercase tracking-wider font-bold text-slate-400">URL pulsante CTA</Label>
                <Input
                  data-testid="input-cta-url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  className="bg-[#0a1020] border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-[12px] uppercase tracking-wider font-bold text-slate-400">Testo pulsante</Label>
                <Input
                  data-testid="input-cta-label"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  className="bg-[#0a1020] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button onClick={() => setStep(1)} variant="outline" className="border-white/10 text-white hover:bg-white/5">
              ← Indietro
            </Button>
            <Button
              data-testid="next-step-3"
              onClick={() => setStep(3)}
              disabled={!nome.trim() || !subject.trim() || htmlBody.length < 10}
              className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold disabled:opacity-40"
            >
              Avanti → Conferma e invia
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Conferma */}
      {step === 3 && preview && (
        <div className="space-y-4 max-w-2xl mx-auto" data-testid="step-conferma">
          <h3 className="font-extrabold text-lg">3. Conferma e invia</h3>

          <div className="bg-[#0a1020] rounded-xl p-5 border border-white/5 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Campagna</div>
              <div className="font-bold text-lg">{nome}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Oggetto</div>
              <div className="text-[14px]">{subject}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Destinatari</div>
              <div className="text-2xl font-extrabold text-[#fbbf24]">{preview.valid_contacts.length} email</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Stima invio</div>
              <div className="text-[13px] text-slate-300">~{Math.ceil(preview.valid_contacts.length / 10)} secondi (10 email/sec)</div>
            </div>
          </div>

          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={gdprConfirmed}
                onChange={(e) => setGdprConfirmed(e.target.checked)}
                data-testid="gdpr-checkbox"
                className="mt-1 h-5 w-5 accent-[#f59e0b]"
              />
              <span className="text-[13px] text-red-200 leading-relaxed">
                <strong>Dichiaro</strong>, sotto la mia responsabilità, di possedere il <strong>consenso GDPR esplicito</strong> di tutti
                i contatti caricati per finalità di marketing/informativa.
                Comprendo che l&apos;invio massivo a contatti senza consenso può comportare <strong>sanzioni del Garante della Privacy</strong> fino a 20 milioni di euro.
              </span>
            </label>
          </div>

          {createError && <div className="text-red-400 text-sm" data-testid="create-error">❌ {createError}</div>}

          <div className="flex justify-between">
            <Button onClick={() => setStep(2)} variant="outline" className="border-white/10 text-white hover:bg-white/5">
              ← Indietro
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => createCampaign(false)}
                disabled={!gdprConfirmed || creating}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                data-testid="save-draft-btn"
              >
                Salva come bozza
              </Button>
              <Button
                onClick={() => createCampaign(true)}
                disabled={!gdprConfirmed || creating}
                className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold disabled:opacity-40"
                data-testid="send-now-btn"
              >
                <Send className="h-4 w-4 mr-1" /> {creating ? "Invio..." : "Crea e invia ora"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CampagnaDetail({ campaignId, onBack }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/campaigns/${campaignId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (data?.status === "in_corso") load();
    }, 2500);
    return () => clearInterval(t);
  }, [data?.status]);

  const sendNow = async () => {
    await axios.post(`${API}/api/admin/campaigns/${campaignId}/send`, {}, { withCredentials: true });
    load();
  };

  if (!data) return <div className="text-slate-400 text-sm">Caricamento...</div>;
  const pct = data.total > 0 ? Math.round(((data.progress.sent + data.progress.failed) / data.total) * 100) : 0;

  return (
    <div data-testid="campagna-detail" className="space-y-5">
      <button onClick={onBack} className="text-[12px] text-slate-400 hover:text-white flex items-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> Torna alle campagne
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-extrabold">{data.nome}</h2>
          <p className="text-[13px] text-slate-400 mt-1">{data.subject}</p>
        </div>
        {data.status === "bozza" && (
          <Button onClick={sendNow} className="bg-[#f59e0b] text-[#0a1020] hover:bg-[#fbbf24] font-extrabold" data-testid="detail-send-btn">
            <Send className="h-4 w-4 mr-1" /> Invia ora
          </Button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Stato</div>
          <div className="text-lg font-extrabold mt-1 capitalize" data-testid="detail-status">{data.status.replace("_", " ")}</div>
        </div>
        <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Totale</div>
          <div className="text-2xl font-extrabold mt-1">{data.total}</div>
        </div>
        <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Inviati</div>
          <div className="text-2xl font-extrabold text-green-400 mt-1">{data.progress.sent}</div>
        </div>
        <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Falliti</div>
          <div className="text-2xl font-extrabold text-red-400 mt-1">{data.progress.failed}</div>
        </div>
      </div>

      {data.status === "in_corso" && (
        <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
          <div className="flex justify-between text-[12px] text-slate-400 mb-2">
            <span>Avanzamento</span>
            <span className="font-bold">{pct}%</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#f59e0b] to-[#fbbf24] transition-all" style={{ width: `${pct}%` }}></div>
          </div>
        </div>
      )}

      <div className="bg-[#0a1020] rounded-xl p-4 border border-white/5">
        <h3 className="font-extrabold mb-3">Ultimi 20 invii</h3>
        {data.last_contacts.length === 0 ? (
          <div className="text-slate-500 text-[13px]">Nessun invio ancora.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Nome</th>
                <th className="text-center py-2">Stato</th>
                <th className="text-left py-2">Inviato</th>
              </tr>
            </thead>
            <tbody>
              {data.last_contacts.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="py-2 text-slate-300">{c.email}</td>
                  <td className="py-2 text-slate-400">{c.nome} {c.cognome}</td>
                  <td className="py-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      c.status === "sent" ? "bg-green-500/20 text-green-400" :
                      c.status === "failed" ? "bg-red-500/20 text-red-400" :
                      c.status === "unsubscribed" ? "bg-amber-500/20 text-amber-400" :
                      "bg-slate-500/20 text-slate-400"
                    }`}>{c.status}</span>
                  </td>
                  <td className="py-2 text-slate-500">{c.sent_at ? new Date(c.sent_at).toLocaleString("it-IT") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// GESTIONALE — Dashboard ispirata a ESSE Organizer
// ============================================================================
function GestionaleHomeSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [movimenti, setMovimenti] = useState([]);
  const [cal, setCal] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/api/admin/gestionale/stats`, { withCredentials: true }),
      axios.get(`${API}/api/admin/gestionale/movimenti?limit=12`, { withCredentials: true }),
      axios.get(`${API}/api/admin/gestionale/calendario`, { withCredentials: true }),
    ]).then(([s, m, c]) => {
      if (!mounted) return;
      setStats(s.data);
      setMovimenti(m.data || []);
      setCal(c.data || []);
    }).catch(() => {}).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [API]);

  if (loading) return <div className="text-slate-400 text-sm">Caricamento gestionale...</div>;
  if (!stats) return <div className="text-red-400 text-sm">Errore caricamento dati gestionale</div>;

  const pctDisp = stats.disponibili.percentuale_disponibilita;

  return (
    <div data-testid="gestionale-home" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-extrabold">Home Gestionale</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Dashboard amministrativa centralizzata — ispirato al modello ESSE Organizer.</p>
        </div>
        <div className="text-right">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Connesso come</div>
          <div className="text-[13px] font-bold text-[#fbbf24]">B0196 · Admin Amministratore</div>
        </div>
      </div>

      {/* 4 STATS BOX */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* GENERICI */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-[#0e1424] px-4 py-2.5 text-[10.5px] uppercase tracking-wider font-extrabold text-slate-300">Generici</div>
          <div className="p-4 space-y-2 text-[13px]">
            <Row k="Attivi (*)" v={stats.generici.attivi} highlight />
            <Row k="Sospesi" v={stats.generici.sospesi} />
            <Row k="Incompleti" v={stats.generici.incompleti} warn={stats.generici.incompleti > 0} />
            <Row k="TOTALI" v={stats.generici.totali} bold />
          </div>
        </div>
        {/* ATTIVI */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-[#0e1424] px-4 py-2.5 text-[10.5px] uppercase tracking-wider font-extrabold text-slate-300">Attivi</div>
          <div className="p-4 space-y-2 text-[13px]">
            <Row k="Ordinari" v={stats.attivi.ordinari} />
            <Row k="Consiglio" v={stats.attivi.consiglio} highlight />
            <Row k="Dirigenti" v={stats.attivi.dirigenti} />
            <Row k="Tecnici / Istruttori" v={`${stats.attivi.tecnici} / ${stats.attivi.istruttori}`} />
            <Row k="Atleti / Volontari" v={`${stats.attivi.atleti} / ${stats.attivi.volontari}`} />
            <Row k="Registri L. 4/13" v={stats.attivi.registri_l4_13} highlight />
          </div>
        </div>
        {/* EXTRA */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-[#0e1424] px-4 py-2.5 text-[10.5px] uppercase tracking-wider font-extrabold text-slate-300">Extra</div>
          <div className="p-4 space-y-2 text-[13px]">
            <Row k="Duplicati" v={stats.extra.duplicati} warn={stats.extra.duplicati > 0} />
            <Row k="Richieste web" v={stats.extra.richieste_web} highlight />
            <Row k="Contratti lavoro" v={stats.extra.contratti_lavoro} />
          </div>
        </div>
        {/* DISPONIBILI con gauge */}
        <div className="bg-gradient-to-br from-[#0a1020] to-[#102a6b]/40 rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-[#0e1424] px-4 py-2.5 text-[10.5px] uppercase tracking-wider font-extrabold text-slate-300">Disponibili</div>
          <div className="p-4 space-y-2 text-[13px]">
            <Row k="Soci + Esterni" v={stats.disponibili.totale_soci_esterni} />
            <Row k="Limite Max" v={stats.disponibili.limite_max} />
            <Row k={`Disponibili ${pctDisp}%`} v={stats.disponibili.disponibili} bold highlight />
            <Gauge percent={pctDisp} />
          </div>
        </div>
      </div>

      {/* RIGA CENTRALE: Comandi rapidi + Calendario + Stato Affiliazione */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Comandi Rapidi */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 p-5 space-y-3" data-testid="comandi-rapidi">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-slate-400">Comandi rapidi</h3>
          <Button className="w-full justify-start bg-[#102a6b] hover:bg-[#1e3a8a] text-white font-bold">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Socio
          </Button>
          <Button className="w-full justify-start bg-[#102a6b] hover:bg-[#1e3a8a] text-white font-bold">
            <Plus className="h-4 w-4 mr-2" /> Registra Entrata
          </Button>
          <Button className="w-full justify-start bg-[#102a6b] hover:bg-[#1e3a8a] text-white font-bold">
            <Plus className="h-4 w-4 mr-2" /> Registra Uscita
          </Button>
        </div>

        {/* Calendario eventi */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 p-5" data-testid="gest-cal-mini">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-slate-400">Calendario</h3>
            <span className="text-[11px] text-[#fbbf24] font-bold">{new Date().toLocaleDateString("it-IT")}</span>
          </div>
          {cal.length === 0 ? (
            <p className="text-[12px] text-slate-500">Nessun evento recente.</p>
          ) : (
            <ul className="space-y-2 max-h-44 overflow-y-auto">
              {cal.slice(0, 8).map((e, i) => (
                <li key={i} className="text-[12px] border-l-2 border-[#f59e0b] pl-2 py-0.5">
                  <div className="text-slate-300">{e.label}</div>
                  <div className="text-[10.5px] text-slate-500">{e.data ? new Date(e.data).toLocaleDateString("it-IT") : "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Stato Affiliazione */}
        <div className="bg-[#0a1020] rounded-xl border border-white/5 p-5 space-y-3" data-testid="stato-affiliazione">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-slate-400">Stato affiliazione e abbonamento</h3>
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-slate-300">Affiliazione</span>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider font-extrabold bg-green-500/20 text-green-400 px-2 py-1 rounded">{stats.stato_affiliazione.stato}</span>
              <span className="text-[11px] text-slate-400">{stats.stato_affiliazione.scadenza}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-slate-300">Abbonamento</span>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider font-extrabold bg-green-500/20 text-green-400 px-2 py-1 rounded">{stats.stato_affiliazione.abbonamento_stato}</span>
              <span className="text-[11px] text-slate-400">{stats.stato_affiliazione.abbonamento_scadenza}</span>
            </div>
          </div>
          <div className="pt-2 border-t border-white/5">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Entrate {stats.entrate_uscite_anno.anno}</div>
            <div className="text-2xl font-extrabold text-[#fbbf24] mt-1">
              € {Number(stats.entrate_uscite_anno.totale_entrate).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-slate-500">{stats.entrate_uscite_anno.numero_movimenti} movimenti</div>
          </div>
        </div>
      </div>

      {/* Movimenti tabella */}
      <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
        <div className="bg-[#0e1424] px-5 py-3 flex items-center justify-between">
          <h3 className="text-[12px] uppercase tracking-[0.2em] font-extrabold text-slate-300">Entrate/Uscite anno solare corrente</h3>
          <span className="text-[11px] text-slate-500">Ultimi {movimenti.length} movimenti</span>
        </div>
        {movimenti.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">Nessun movimento contabile registrato.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-slate-500 text-[10.5px] uppercase tracking-wider border-b border-white/5">
              <tr>
                <th className="text-left px-5 py-2.5">Data</th>
                <th className="text-left px-5 py-2.5">Pacchetto</th>
                <th className="text-left px-5 py-2.5">Metodo</th>
                <th className="text-right px-5 py-2.5">Importo</th>
              </tr>
            </thead>
            <tbody>
              {movimenti.map((m) => (
                <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-2 text-slate-400">{m.data_pagamento ? new Date(m.data_pagamento).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="px-5 py-2 text-slate-300">{m.pacchetto || "—"}</td>
                  <td className="px-5 py-2 text-slate-300">{m.metodo_pagamento || "—"}</td>
                  <td className="px-5 py-2 text-right font-bold text-green-400">+ € {Number(m.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, bold = false, highlight = false, warn = false }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
      <span className={`text-slate-400 ${bold ? "font-bold" : ""}`}>{k}</span>
      <span className={`tabular-nums ${bold ? "text-lg font-extrabold" : "font-bold"} ${highlight ? "text-[#fbbf24]" : warn ? "text-amber-400" : "text-white"}`}>{v}</span>
    </div>
  );
}

function Gauge({ percent = 0 }) {
  const safe = Math.max(0, Math.min(100, percent));
  const color = safe > 60 ? "text-green-400" : safe > 30 ? "text-amber-400" : "text-red-400";
  return (
    <div className="pt-2">
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${safe > 60 ? "bg-green-500" : safe > 30 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${safe}%` }}></div>
      </div>
      <div className={`mt-1.5 text-center text-[11px] font-extrabold ${color}`}>SOCI {safe}% liberi</div>
    </div>
  );
}

function GestionaleAmministrazione() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [mov, setMov] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/admin/gestionale/movimenti?limit=100`, { withCredentials: true })
      .then((r) => setMov(r.data || []))
      .catch(() => {});
  }, [API]);

  const totale = mov.reduce((s, m) => s + Number(m.importo || 0), 0);

  return (
    <div data-testid="gestionale-amministrazione" className="space-y-5">
      <h2 className="text-3xl font-extrabold">Amministrazione</h2>
      <p className="text-[13.5px] text-slate-400">Movimenti contabili associativi (entrate da iscrizioni, pagamenti, rinnovi).</p>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Totale entrate" value={`€ ${totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} color="green" />
        <Stat label="Numero movimenti" value={mov.length} color="blue" />
        <Stat label="Media per movimento" value={`€ ${(mov.length ? totale / mov.length : 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} color="amber" />
      </div>

      <div className="bg-[#0a1020] rounded-xl border border-white/5 overflow-hidden">
        <div className="bg-[#0e1424] px-5 py-3 text-[12px] uppercase tracking-wider font-extrabold text-slate-300">Tutti i movimenti</div>
        {mov.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">Nessun movimento.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-slate-500 text-[10.5px] uppercase tracking-wider border-b border-white/5">
              <tr>
                <th className="text-left px-5 py-2.5">Data</th>
                <th className="text-left px-5 py-2.5">User ID</th>
                <th className="text-left px-5 py-2.5">Pacchetto</th>
                <th className="text-left px-5 py-2.5">Metodo</th>
                <th className="text-right px-5 py-2.5">Importo</th>
              </tr>
            </thead>
            <tbody>
              {mov.map((m) => (
                <tr key={m.id} className="border-b border-white/5">
                  <td className="px-5 py-2 text-slate-400">{m.data_pagamento ? new Date(m.data_pagamento).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="px-5 py-2 text-slate-500 font-mono text-[11px]">{(m.user_id || "").slice(0, 8)}…</td>
                  <td className="px-5 py-2 text-slate-300">{m.pacchetto || "—"}</td>
                  <td className="px-5 py-2 text-slate-300">{m.metodo_pagamento || "—"}</td>
                  <td className="px-5 py-2 text-right font-bold text-green-400">+ € {Number(m.importo).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color = "blue" }) {
  const colors = {
    green: "text-green-400 border-green-500/30",
    blue: "text-blue-400 border-blue-500/30",
    amber: "text-[#fbbf24] border-amber-500/30",
  };
  return (
    <div className={`bg-[#0a1020] rounded-xl border ${colors[color] || colors.blue} p-5`}>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className={`text-3xl font-extrabold mt-1 ${colors[color]?.split(" ")[0] || ""}`}>{value}</div>
    </div>
  );
}

function GestionaleCalendario() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/admin/gestionale/calendario`, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, [API]);

  return (
    <div data-testid="gestionale-calendario" className="space-y-5">
      <h2 className="text-3xl font-extrabold">Calendario</h2>
      <p className="text-[13.5px] text-slate-400">Eventi recenti dell&apos;associazione: nuove iscrizioni, spedizioni, scadenze.</p>

      <div className="bg-[#0a1020] rounded-xl border border-white/5 p-5">
        {items.length === 0 ? (
          <p className="text-slate-500 text-sm">Nessun evento.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((e, i) => (
              <li key={i} className="flex items-start gap-3 border-l-2 border-[#f59e0b] pl-3 py-1.5">
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-white">{e.label}</div>
                  <div className="text-[11px] text-slate-500">{e.data ? new Date(e.data).toLocaleString("it-IT") : "—"}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-white/5 text-slate-300">{e.tipo}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function GestionalePlaceholder({ titolo, descrizione, link, linkLabel }) {
  return (
    <div data-testid={`gestionale-placeholder-${titolo.toLowerCase().replace(/\s+/g, "-")}`} className="space-y-5">
      <h2 className="text-3xl font-extrabold">{titolo}</h2>
      <p className="text-[13.5px] text-slate-400">{descrizione}</p>

      <div className="bg-gradient-to-br from-[#0a1020] to-[#102a6b]/30 rounded-2xl border border-white/5 p-10 text-center">
        <Sparkles className="h-10 w-10 text-[#f59e0b] mx-auto mb-3" />
        <h3 className="text-xl font-extrabold text-white">Sezione in arrivo</h3>
        <p className="text-[13.5px] text-slate-400 mt-2 max-w-xl mx-auto leading-relaxed">
          Questa sezione del gestionale è in fase di sviluppo. La struttura è già pronta —
          completeremo le funzionalità in modo incrementale partendo dalle priorità che ci indicherai.
        </p>
        {link && (
          <a href={`#${link}`} className="inline-block mt-5 text-[#fbbf24] hover:text-white text-[13px] font-bold underline underline-offset-4">
            → {linkLabel}
          </a>
        )}
      </div>
    </div>
  );
}

